import type { CheckCartItem } from '../types/checkCart'

/**
 * Calculates the total price for a single check cart item including all modifiers
 * This unified function works for both products and combos
 */
export function calculateCheckCartItemTotal(item: CheckCartItem): number {
  let itemTotal = item.base_price * item.quantity

  // Add modifier pricing for products (new unified structure - direct on cart item)
  if (item.modifiers && Array.isArray(item.modifiers)) {
    item.modifiers.forEach((modifier) => {
      if (modifier.selected) {
        const selections = Array.isArray(modifier.selected)
          ? modifier.selected
          : [modifier.selected]

        selections.forEach((selection) => {
          // Add price from modifier selection
          if (selection.price) {
            itemTotal += selection.price * (selection.quantity || 1) * item.quantity
          }
          // Add price modifier from modifier selection (handle both property names)
          const priceChange = selection.price_modifier || selection.price_change || 0
          if (priceChange !== 0) {
            itemTotal += priceChange * item.quantity
          }
        })
      }
    })
  }

  // Add customizations from all items (unified modifier system for combos and legacy products)
  if (item.items && Array.isArray(item.items)) {
    item.items.forEach((subItem) => {
      // Add price variation for combo options
      if (subItem.price_variation && typeof subItem.price_variation === 'number') {
        itemTotal += subItem.price_variation * item.quantity
      }

      // Add modifier pricing (legacy structure for backward compatibility)
      if (subItem.modifiers && Array.isArray(subItem.modifiers)) {
        subItem.modifiers.forEach((modifier) => {
          if (modifier.selected) {
            const selections = Array.isArray(modifier.selected)
              ? modifier.selected
              : [modifier.selected]

            selections.forEach((selection) => {
              // Add price from modifier selection
              if (selection.price) {
                itemTotal += selection.price * (selection.quantity || 1) * item.quantity
              }
              // Add price modifier from modifier selection (handle both property names)
              const priceChange = selection.price_modifier || selection.price_change || 0
              if (priceChange !== 0) {
                itemTotal += priceChange * item.quantity
              }
            })
          }
        })
      }

      // Add CUSTOM_COMBO selected_modifiers pricing
      if (subItem.selected_modifiers && Object.keys(subItem.selected_modifiers).length > 0) {
        Object.values(subItem.selected_modifiers).forEach((selections) => {
          if (Array.isArray(selections)) {
            selections.forEach((selection: any) => {
              // Add price from modifier selection
              if (selection.price) {
                itemTotal += selection.price * (selection.quantity || 1) * subItem.quantity * item.quantity
              }
              // Add price modifier from modifier selection
              const priceChange = selection.price_modifier || selection.price_change || 0
              if (priceChange !== 0) {
                itemTotal += priceChange * subItem.quantity * item.quantity
              }
            })
          }
        })
      }
    })
  }

  return itemTotal
}

/**
 * Calculate check cart total
 */
export function calculateCheckCartTotal(cartItems: CheckCartItem[]): string {
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return '0.00'
  }

  const total = cartItems.reduce((accumulator, item) => {
    // Use the pre-calculated total if available, but recalculate for CUSTOM_COMBO to ensure modifiers are included
    if (item.total && typeof item.total === 'number' && item.type !== 'CUSTOM_COMBO') {
      return accumulator + item.total
    }

    return accumulator + calculateCheckCartItemTotal(item)
  }, 0)

  return Math.round(total).toString()
}
